/**
 * Rules for HubSpot file-upload fields.
 *
 * One module on purpose: the browser checks these to give immediate feedback
 * (`FieldRenderer`), and `/api/hubspot/submit` checks them again as the actual
 * trust boundary. Same predicate, two call sites — never two sets of rules.
 */

export const MAX_UPLOAD_MB = 20;
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

export const ALLOWED_UPLOAD_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "ppt",
  "pptx",
  "xls",
  "xlsx",
  "zip",
  "jpg",
  "jpeg",
  "png",
] as const;

/** `accept` attribute for the file input. */
export const UPLOAD_ACCEPT = ALLOWED_UPLOAD_EXTENSIONS.map(
  (ext) => `.${ext}`
).join(",");

export type UploadRejection = "too_large" | "type";

/**
 * `null` when the file is acceptable, otherwise why it was rejected.
 * Extension-based: the browser-reported MIME type is trivially spoofed and
 * inconsistent across platforms (`.docx` alone has three common values), so it
 * would add no security while rejecting legitimate files.
 */
export function rejectUpload(file: {
  name: string;
  size: number;
}): UploadRejection | null {
  if (file.size > MAX_UPLOAD_BYTES) return "too_large";

  const ext = file.name.includes(".")
    ? (file.name.split(".").pop() ?? "").toLowerCase()
    : "";
  return (ALLOWED_UPLOAD_EXTENSIONS as readonly string[]).includes(ext)
    ? null
    : "type";
}

/** Multipart part name carrying the file for form field `name`. */
export const uploadPartName = (name: string) => `file:${name}`;
